import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { connect } from "react-redux";
import clsx from "clsx";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Badge from "@mui/material/Badge";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";

import LicenseRestriction from "views/Components/License";
import Zendesk from "./Components/Zendesk";

import {
  isSecondLevelSelected,
  getMenu,
  renderSpacerGridItem,
} from "../utils/sidebarUtils";

const PREFIX = "SidebarView2";

const classes = {
  title: `${PREFIX}-title`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  drawerPaperClose: `${PREFIX}-drawerPaperClose`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  sideNav: `${PREFIX}-sideNav`,
  selectedMenuItem: `${PREFIX}-selectedMenuItem`,
  menuItem: `${PREFIX}-menuItem`,
};

const Root = styled(Drawer)(({ theme }) => ({
  [`& .${classes.title}`]: {
    flexGrow: 1,
  },
  [`& .${classes.drawerPaper}`]: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  [`& .${classes.drawerPaperClose}`]: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(8),
  },
  [`& .${classes.appBarSpacer}`]: theme.mixins.toolbar,
  [`& .${classes.sideNav}`]: {
    paddingBottom: 300,
  },
  [`& .${classes.selectedMenuItem}`]: {
    backgroundColor: theme.palette.action.hover,
  },
  [`& .${classes.menuItem}`]: {
    textTransform: "uppercase",
  },
}));

const drawerWidth = 220;

const SidebarView = (props) => {
  const [open, setOpen] = useState(true);
  const [menuItems, setMenuItems] = useState([]);

  const [selectedMenuItem, setSelectedMenuItem] = useState({
    selected: {
      toplevel: props.payload.toplevel,
      secondlevel: props.payload.secondlevel,
    },
    previous: null,
  });

  const [exit, setExit] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    let newMenuItems = getMenu(props.orgId, props.settings);

    setMenuItems(newMenuItems);
  }, [props.orgId]);

  useEffect(() => {
    setSelectedMenuItem({
      selected: {
        toplevel: props.payload.toplevel,
        secondlevel: props.payload.secondlevel,
      },
      previous: selectedMenuItem.previous,
    });
  }, [props.payload]);

  const handleDrawerOpen = () => {
    setSelectedMenuItem({
      selected: selectedMenuItem.previous,
      previous: null,
    });
    setOpen(true);
  };

  const handleDrawerClose = () => {
    handleMenuSelect("");
    setOpen(false);
  };

  const handleDrawerToggle = () => {
    if (open) {
      handleDrawerClose();
    } else {
      handleDrawerOpen();
    }
  };

  const handleMenuSelect = (menuType) => {
    if (open === false) {
      setOpen(true);
    }
    if (menuType === selectedMenuItem.selected.toplevel) {
      menuType = "";
    }
    setSelectedMenuItem({
      selected: {
        toplevel: menuType,
        secondlevel: selectedMenuItem.selected.secondlevel,
      },
      previous: selectedMenuItem.selected,
    });
  };

  const handleMenuNavigate = (menuType, childMenuType) => {
    props.dispatch({
      type: "PAGE",
      pageHash: {},
      payload: {
        orgId: props.orgId,
        toplevel: menuType,
        secondlevel: childMenuType,
      },
    });
  };

  return (
    <Root
      variant="permanent"
      classes={{
        paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
      }}
      open={open}
    >
      <div>
        <div className={classes.appBarSpacer} />
        <LicenseRestriction
          features={["AUTOBOT"]}
          renderAllow={renderSpacerGridItem}
          renderDisallow={() => null}
        />
        <List className={classes.sideNav}>
          <ListItem
            key="menu-item-drawer"
            button
            onMouseOver={() => setExit(true)}
            onClick={handleDrawerToggle}
          >
            <ListItemIcon>
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </ListItemIcon>
          </ListItem>

          {menuItems.map((menuItem) => (
            <div key={`menu-item-${menuItem.type}`}>
              <ListItem
                button
                data-cy={`${menuItem.type}`} // for selecting with cypress
                onClick={() => handleMenuSelect(menuItem.type)}
              >
                <ListItemIcon>
                  <Icon
                    className={menuItem.icon}
                    style={{ width: "100%", textAlign: "left" }}
                  />
                </ListItemIcon>
                <ListItemText
                  className={classes.menuItem}
                  primary={menuItem.title}
                  secondary={menuItem.subtitle}
                />
                {menuItem.badge && (
                  <Badge
                    style={{ marginRight: 10 }}
                    badgeContent={menuItem.badge}
                    color="primary"
                  />
                )}
              </ListItem>
              <Collapse
                in={selectedMenuItem.selected.toplevel === menuItem.type}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {menuItem.children.map((childMenuItem) => (
                    <ListItem
                      button
                      data-cy={`${childMenuItem.type}`} // for selecting with cypress
                      data-license={`${menuItem.type}`} // for selecting in license cypress test
                      key={`menu-item-${childMenuItem.type}`}
                      className={
                        isSecondLevelSelected(
                          childMenuItem,
                          selectedMenuItem.selected.secondlevel
                        )
                          ? classes.selectedMenuItem
                          : ""
                      }
                      onClick={() =>
                        handleMenuNavigate(menuItem.type, childMenuItem.type)
                      }
                    >
                      <ListItemIcon />
                      <Badge
                        badgeContent={childMenuItem.badge}
                        color="primary"
                        overlap={"rectangular"}
                      >
                        <ListItemText
                          primaryTypographyProps={{ variant: "overline" }}
                          primary={childMenuItem.title}
                        />
                      </Badge>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </div>
          ))}
        </List>
      </div>
      {
        <Zendesk
          open={open}
          licenseLabel={props.licenseLabel}
          license={props.license}
        />
      }
    </Root>
  );
};

const mapStateToProps = (state) => {
  return {
    orgId: state.location.payload.orgId,
    payload: state.location.payload,
    settings: state.session.settings,
  };
};
export default connect(mapStateToProps)(SidebarView);
